/**
 * Labels of the customer websites, apps and table QR pages (ADR 0071, FR-ONL-19), apart from the staff apps' i18next setup so
 * a website page carries only these. One JSON file per area and language (`messages/{language}/{area}.json`), loaded by
 * language: adding a language is adding its folder and listing it in `languages.json`, no code change
 * (docs/architecture/CUSTOMER_LANGUAGES.md). Keys are type-checked against the English files.
 */
import settings from './languages.json';
import type account from './messages/en/account.json';
import type booking from './messages/en/booking.json';
import type cart from './messages/en/cart.json';
import type checkout from './messages/en/checkout.json';
import type common from './messages/en/common.json';
import type content from './messages/en/content.json';
import type home from './messages/en/home.json';
import type menu from './messages/en/menu.json';
import type qr from './messages/en/qr.json';
import type rewards from './messages/en/rewards.json';
import type seo from './messages/en/seo.json';
import type stores from './messages/en/stores.json';

/** The areas of the customer apps, one file each per language. New screens go in one of these; a new area is added here. */
export const AREAS = ['common', 'home', 'stores', 'menu', 'cart', 'checkout', 'booking', 'account', 'rewards', 'content', 'qr', 'seo'] as const;
export type Area = (typeof AREAS)[number];

type Catalog = {
  common: typeof common;
  home: typeof home;
  stores: typeof stores;
  menu: typeof menu;
  cart: typeof cart;
  checkout: typeof checkout;
  booking: typeof booking;
  account: typeof account;
  rewards: typeof rewards;
  content: typeof content;
  qr: typeof qr;
  seo: typeof seo;
};

/** `area.key` and `area.group.key` paths of the English files; a plural's `_one`/`_other` forms are named without the suffix. */
type Paths<T, P extends string = ''> = {
  [K in keyof T & string]: T[K] extends string
    ? `${P}${K extends `${infer B}_${'one' | 'other' | 'zero' | 'two' | 'few' | 'many'}` ? B : K}`
    : Paths<T[K], `${P}${K}.`>;
}[keyof T & string];

export type StorefrontKey = Paths<Catalog>;

/** Every area's texts for one language, as JSON objects. */
export type StorefrontMessages = Record<Area, Record<string, unknown>>;

/** The customer languages the platform offers (`languages.json`), in order; each brand site offers some of them. */
export const CUSTOMER_LANGUAGES: readonly string[] = settings.languages;

/** The platform's default customer language. */
export const DEFAULT_CUSTOMER_LANGUAGE: string = settings.default;

export function isCustomerLanguage(value: unknown): value is string {
  return typeof value === 'string' && CUSTOMER_LANGUAGES.includes(value);
}

/** Every area's texts in <paramref name="language"/>, read from its files; a language not on the list loads the default. */
export async function loadStorefrontMessages(language: string): Promise<StorefrontMessages> {
  const lang = isCustomerLanguage(language) ? language : DEFAULT_CUSTOMER_LANGUAGE;
  const files = await Promise.all(
    AREAS.map(async (area) => [area, ((await import(`./messages/${lang}/${area}.json`)) as { default: Record<string, unknown> }).default] as const),
  );
  return Object.fromEntries(files) as StorefrontMessages;
}

export type StorefrontT = (key: StorefrontKey, values?: Record<string, string | number>) => string;

function lookup(messages: StorefrontMessages, path: string): string | undefined {
  let node: unknown = messages;
  for (const part of path.split('.')) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return typeof node === 'string' ? node : undefined;
}

/**
 * A translator over <paramref name="messages"/>: `{{name}}` placeholders, plural forms by `count` (`key_one`, `key_other`…, by
 * the language's rules), and <paramref name="fallback"/>'s text (the default language) where a key is missing.
 */
export function storefrontTranslator(language: string, messages: StorefrontMessages, fallback?: StorefrontMessages): StorefrontT {
  const plurals = new Intl.PluralRules(language);
  return (key, values) => {
    const count = values?.count;
    const forms = typeof count === 'number' ? [`${key}_${plurals.select(count)}`, `${key}_other`, key] : [key];
    let text: string | undefined;
    for (const source of fallback ? [messages, fallback] : [messages]) {
      for (const form of forms) {
        text ??= lookup(source, form);
      }
    }

    return (text ?? key).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name: string) =>
      values && name in values ? String(values[name]) : match,
    );
  };
}

/**
 * <paramref name="path"/> in <paramref name="language"/>: the default language has no prefix (`/stores`), the others do
 * (`/en/stores`). The path given has none.
 */
export function localePath(language: string, path: string, defaultLanguage: string = DEFAULT_CUSTOMER_LANGUAGE): string {
  const clean = path.startsWith('/') ? path : `/${path}`;
  return language === defaultLanguage ? clean : `/${language}${clean === '/' ? '' : clean}`;
}

/** The language a path starts with (`/en/stores` → `en`) and the rest; none for the default language's paths. */
export function splitLocalePath(path: string): { language?: string; rest: string } {
  const [, first, ...rest] = path.split('/');
  return first && first !== DEFAULT_CUSTOMER_LANGUAGE && isCustomerLanguage(first)
    ? { language: first, rest: `/${rest.join('/')}` }
    : { rest: path };
}
